import React, { FC, useContext } from 'react'
import { Plural, t, Trans } from '@lingui/macro'
import { FollowResourceType } from 'front-end/src/graphql/private/PrivateSchemaTypes'
import Rating from '../../components/common/rating/Rating'
import { CommentsFooter } from '../../components/common/comments/comments-footer/CommentsFooter'
import { PathwayCompletedMessage } from '../student-pathways-page/components/PathwayCompletedMessage'
import UpNext from './components/UpNext'
import { RetryResourceQuiz } from './RetryResourceQuiz'
import {
  QuizElementTypes,
  TopicElementTypeEnum
} from 'types/TopicElementTypeEnum'
import TriangleTop from './components/TriangleTop'
import QuizScore from './components/quizes/quizScore/QuizScore'
import IconTick from '../../components/common/icon/IconTick'
import { TopicDetail, TopicDetailContext } from './TopicDetailContext'
import { TopicPageMode } from './TopicDetailPage'
import TopicCPDMarkToggle from 'front-end/src/pages/topic-detail-page/components/TopicCPDMarkToggle'
import { Box, Flex } from 'front-end/src/components'
import {
  fontSizes,
  fontWeights
} from 'front-end/src/components/chakraThemeExtension'
import { brandColors } from 'shared/basicPalette'

export const showNotCompletedAlert = (
  enrolmentOrSelfEnrol: TopicDetail['enrolmentOrSelfEnrol']
) => {
  const mandatoryQuizzes =
    enrolmentOrSelfEnrol?.topicVersion?.publishedElements?.filter((e) =>
      QuizElementTypes.includes(e.type as unknown as TopicElementTypeEnum)
    ) || []

  const mandatoryQuizzesIds = mandatoryQuizzes.map((q) => q.id)
  const mandatoryQuizResponses =
    enrolmentOrSelfEnrol?.quizResponses?.filter(
      (r) =>
        r.topic_element_id &&
        mandatoryQuizzesIds &&
        mandatoryQuizzesIds.includes(r.topic_element_id)
    ) || []

  return mandatoryQuizzes.length > mandatoryQuizResponses.length
}

interface TopicEnrolment {
  auto_correct: number
  rating?: number | null | undefined
  available_points: number
  pass_mark: any
  completion_date?: string
}

export const TopicCompletionMessage: FC<{
  topicEnrolment: TopicEnrolment
  topicId: number
  allowRetry?: boolean
  attemptsLeft?: number
  inPathway?: boolean
}> = ({ topicEnrolment, topicId, allowRetry, inPathway, attemptsLeft }) => {
  const correctPoints = topicEnrolment.auto_correct
  const availablePoints = topicEnrolment.available_points
  const passMark = topicEnrolment.pass_mark
  const passMarkPassed = correctPoints >= passMark

  return availablePoints > 0 ? ( // has available points? topic has a quiz
    <Flex align="center" direction="column">
      <Flex align="center">
        <QuizScore
          percentages={(correctPoints / availablePoints) * 100}
          tagText={passMarkPassed ? t`Passed` : t`Failed`}
          tagColor={passMarkPassed ? 'green' : 'red'}
        />
        <Box
          ml="24px"
          size="lg"
          fontSize={fontSizes['2xl']}
          fontWeight={fontWeights.medium}
        >
          {passMark ? (
            <>
              {passMarkPassed ? (
                <Trans>You have passed the quiz</Trans>
              ) : (
                <Trans>You have not passed the quiz</Trans>
              )}
            </>
          ) : (
            <Trans>You have completed this quiz</Trans>
          )}
        </Box>
      </Flex>

      {!!passMark && (attemptsLeft === undefined || attemptsLeft > 0) && (
        <Box mt="24px">
          {!passMarkPassed && allowRetry && (
            <RetryResourceQuiz topicId={topicId} />
          )}
        </Box>
      )}

      {!passMarkPassed && !!attemptsLeft && attemptsLeft > 0 && (
        <Box mt="8px" color={brandColors.grey.mid}>
          <Plural
            value={attemptsLeft}
            one="# attempt left"
            other="# attempts left"
          />
        </Box>
      )}
    </Flex>
  ) : (
    <Flex
      justify="center"
      size="lg"
      fontSize={fontSizes['2xl']}
      fontWeight={fontWeights.medium}
    >
      {!inPathway && <IconTick boxSize="32px" mr="24px" />}{' '}
      <Trans>You have completed this resource</Trans>
    </Flex>
  )
}

export const TopicCompletionPage = () => {
  const { topic, pathway, nextInPathway, topicVersion, mode } =
    useContext(TopicDetailContext)

  if (!topic) {
    return null
  }

  const enrolmentOrSelfEnrol = topic.enrolmentOrSelfEnrol

  const correctPoints = enrolmentOrSelfEnrol?.auto_correct || 0
  const passMark = enrolmentOrSelfEnrol?.pass_mark || 0
  const passMarkPassed = correctPoints >= passMark

  if (!topicVersion) {
    return null
  }

  const CommentsWrapper = () => {
    if (!topic) {
      return null
    }

    return (
      <Box className="comments-page-wrapper">
        <CommentsFooter
          itemType={FollowResourceType.topic}
          itemId={Number(topic.id)}
          allowComments={!!topic.allow_comments}
        />
      </Box>
    )
  }

  if (
    !enrolmentOrSelfEnrol &&
    mode !== TopicPageMode.Preview &&
    mode !== TopicPageMode.VersionView
  ) {
    return (
      <Box className="page page-system-generated-completion">
        <CommentsWrapper />
      </Box>
    )
  }

  if (showNotCompletedAlert(enrolmentOrSelfEnrol)) {
    return (
      <Box className="page page-system-generated-completion">
        <Box className="page-content page-content-completion">
          <Box className="topic-completion-page pageElement learn">
            <Box className="topic-completion-alert">
              <TriangleTop backgroundColor="#e3e8ea" />
              <h2>
                <Trans>Please answer every question</Trans>
              </h2>
              <p>
                <Trans>
                  You will not have completed this resource until all questions
                  have been answered.
                </Trans>
              </p>
            </Box>
          </Box>
        </Box>
        <CommentsWrapper />
      </Box>
    )
  } else {
    if (!topic || !topic.enrolmentOrSelfEnrol) {
      return null
    }

    const maxAttempts = topic.currentTopicVersion?.max_attempts
    const attemptsLeft = maxAttempts
      ? maxAttempts - (topic.enrolmentOrSelfEnrol?.attempt || 0)
      : undefined

    const isCompleted = enrolmentOrSelfEnrol?.completion_date

    return (
      <Box className="page page-system-generated-completion">
        <Box className="page-content page-content-completion">
          <Box className="topic-completion-page pageElement learn">
            {isCompleted && (
              <TopicCompletionMessage
                topicEnrolment={enrolmentOrSelfEnrol as TopicEnrolment}
                topicId={topic.id}
                allowRetry={topic.currentTopicVersion?.allow_retry}
                attemptsLeft={attemptsLeft}
                inPathway={!!pathway}
              />
            )}
          </Box>
          {pathway &&
          passMark &&
          !passMarkPassed &&
          pathway.progress === 'locked' ? (
            <PathwayCompletedMessage pathwayId={pathway.id} />
          ) : (
            <>
              {pathway && nextInPathway && nextInPathway.id !== topic.id && (
                <Box className="upnext-page-wrapper">
                  <UpNext pathway={pathway} topic={nextInPathway} />
                </Box>
              )}
              {pathway &&
                !nextInPathway &&
                enrolmentOrSelfEnrol?.progress === 100 && (
                  <PathwayCompletedMessage pathwayId={pathway.id} />
                )}
              {enrolmentOrSelfEnrol?.completion_date &&
                !enrolmentOrSelfEnrol?.rating &&
                topic.enrolmentOrSelfEnrol.id && (
                  <Rating
                    key={topic.id} // key is crucial here because the rating component needs to be remounted if a topic is replaced in the store
                    enrolmentId={topic.enrolmentOrSelfEnrol.id}
                  />
                )}
            </>
          )}
          {isCompleted &&
            typeof topic.enrolmentOrSelfEnrol?.isCpdEntry === 'boolean' && (
              <TopicCPDMarkToggle
                initialCpdStatus={topic.enrolmentOrSelfEnrol.isCpdEntry}
                topicEnrolmentId={topic.enrolmentOrSelfEnrol.id}
                key={topic.enrolmentOrSelfEnrol.id}
              />
            )}
          <CommentsWrapper />
        </Box>
      </Box>
    )
  }
}
